import { useState, useEffect, useCallback } from "react";

type PaginationLinks = {
  next: string | null;
  prev: string | null;
};

type PaginationMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

type UsePaginationProps = {
  meta?: PaginationMeta;
  links?: PaginationLinks;
  onPageUpdate: (page: number) => void;
};

export function usePagination({
  meta,
  links,
  onPageUpdate,
}: UsePaginationProps) {
  const [currentPage, setCurrentPage] = useState(meta?.current_page ?? 1);

  useEffect(() => {
    if (meta?.current_page !== undefined) {
      setCurrentPage(meta.current_page);
    }
  }, [meta?.current_page]);

  const totalItems = meta?.total ?? 0;
  const pageSize = meta?.per_page ?? 10;
  const totalPages = meta?.last_page ?? 1;
  const hasNextPage = links?.next !== null && links?.next !== undefined;
  const hasPreviousPage = links?.prev !== null && links?.prev !== undefined;

  const onNext = useCallback(() => {
    if (hasNextPage && currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      onPageUpdate(nextPage);
    }
  }, [currentPage, hasNextPage, onPageUpdate, totalPages]);

  const onPrevious = useCallback(() => {
    if (hasPreviousPage && currentPage > 1) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      onPageUpdate(prevPage);
    }
  }, [currentPage, hasPreviousPage, onPageUpdate]);

  const onPageChange = useCallback(
    (page: number) => {
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
        onPageUpdate(page);
      }
    },
    [onPageUpdate, totalPages],
  );

  return {
    currentPage,
    totalItems,
    hasNextPage,
    hasPreviousPage,
    onNext,
    onPrevious,
    onPageChange,
    pageSize,
    totalPages,
  };
}
